import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { useSwiftypeApi } from './swiftype-search/use-swiftype-api';
import { SwiftypePage } from './swiftype-search/models';
import { useSearchKeybindings } from './swiftype-search/use-search-keybindings';

const MAX_RESULTS = 10;

/**
 * The main search component that allows searching for documentation on the
 * site. Displays an input and a list of results when searching.
 */
export function SwiftypeSearch() {
  const [text, setText] = useState<string>('');
  const [pages, setPages] = useState<SwiftypePage[]>([]);
  const [selectedEntryIdx, setSelectedEntryIdx] = useState<number>();
  const [isSearchFocused, setSearchFocused] = useState<boolean>(false);

  // Only show top results, don't let them flow out of the screen;
  // the user can hit enter to see a full page view of the results:
  const truncatedPages = pages.slice(0, MAX_RESULTS);

  const searchRef = useRef<HTMLDivElement>(null);

  useSearchKeybindings({
    searchRef,
    isEnabled: isSearchFocused,

    // Closes the query results dropdown on any click outside of the search area:
    onClickOutside: () => setSearchFocused(false),
    onEnter: () => {
      if (selectedEntryIdx !== undefined && pages[selectedEntryIdx]) {
        window.location.href = pages[selectedEntryIdx].url;
      } else {
        // This is a special combined docs + blog search results page:
        window.location.href = `https://chronosphere.io/?s=#stq=${text}&stp=1`;
      }
    },
    onEscape: () => {
      setSearchFocused(false);
    },
    onArrowDown: () => {
      setSelectedEntryIdx((prev) => {
        if (prev === undefined) return 0;
        if (prev + 1 > pages.length - 1) return prev;

        return prev + 1;
      });
    },
    onArrowUp: () => {
      setSelectedEntryIdx((prev) => {
        if (prev === undefined || prev === 0) return;

        return prev - 1;
      });
    },
  });

  useSwiftypeApi({
    text,
    onSuccess: (pages: SwiftypePage[]) => {
      setPages(pages);
      setSelectedEntryIdx(undefined);
    },
    onError: () => {
      setPages([]);
      setSelectedEntryIdx(undefined);
    },
  });

  const onChange = (value: string) => {
    setText(value);
  };

  return (
    <>
      <div
        className={clsx(
          'relative md:w-64',
          'mx-min-w-[200px] hidden md:inline-block'
        )}
        ref={searchRef}
      >
        <div className="text-gray-900 contrast-more:text-gray-800 dark:text-gray-300 contrast-more:dark:text-gray-300">
          <form onSubmit={(e) => e.preventDefault()}>
            <input
              spellCheck={false}
              value={text}
              onClick={() => setSearchFocused(true)}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              className={clsx(
                'block w-full appearance-none rounded-lg px-3 py-2 transition-colors',
                'text-base leading-tight md:text-sm',
                'bg-black/[.05] dark:bg-gray-50/10',
                'focus:bg-white dark:focus:bg-dark',
                'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                'contrast-more:border contrast-more:border-current'
              )}
              type="search"
              placeholder="Search documentation…"
            />
            <div
              className={clsx('relative', isSearchFocused ? 'block' : 'hidden')}
            >
              <ul className="absolute top-2.5 rounded-sm bg-white pl-0 text-xs shadow-md">
                {truncatedPages.map((page, idx) => (
                  <li
                    className={clsx(
                      'border-b border-gray-100 p-2.5 leading-6',
                      idx === selectedEntryIdx ? 'bg-gray-100' : 'bg-inherit'
                    )}
                    key={page.id}
                  >
                    <a
                      className="swiftype-anchor inline-block w-64 truncate whitespace-nowrap align-middle text-sm text-green-500 hover:text-green-500"
                      href={page.url}
                      dangerouslySetInnerHTML={{
                        __html: page.highlight.title ?? page.title,
                      }}
                    />
                    <span className="inline-block w-64 truncate whitespace-nowrap align-middle text-xs">
                      {page.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
